/**
 * PayPal Service
 * Handles PayPal checkout integration
 */

import { authService } from '../auth/auth-service.js';
import { SUPABASE_URL, PAYPAL_CLIENT_ID } from '../config/config.js';
import { entitlementService } from '../entitlements/entitlement-service.js';

class PayPalService {
  constructor() {
    this.initialized = false;
  }

  /**
   * Initialize PayPal SDK
   */
  async init() {
    if (this.initialized) return true;

    if (!PAYPAL_CLIENT_ID || PAYPAL_CLIENT_ID === 'YOUR_PAYPAL_CLIENT_ID') {
      console.warn('[PayPalService] PayPal not configured');
      return false;
    }

    // Load PayPal SDK dynamically
    if (typeof paypal === 'undefined') {
      try {
        if (typeof window.loadPayPalSDK === 'function') {
          await window.loadPayPalSDK(PAYPAL_CLIENT_ID);
        } else {
          console.warn('[PayPalService] PayPal SDK loader not available');
          return false;
        }
      } catch (error) {
        console.error('[PayPalService] Failed to load PayPal SDK:', error);
        return false;
      }
    }

    // Check if PayPal SDK is now loaded
    if (typeof paypal === 'undefined') {
      console.warn('[PayPalService] PayPal SDK failed to load');
      return false;
    }

    this.initialized = true;
    return true;
  }

  /**
   * Check if PayPal is available
   */
  isAvailable() {
    return this.initialized && typeof paypal !== 'undefined';
  }

  /**
   * Create PayPal buttons for a container
   */
  createButtons(containerId, productKey, options = {}) {
    if (!this.isAvailable()) {
      console.error('[PayPalService] PayPal not available');
      return;
    }

    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`[PayPalService] Container ${containerId} not found`);
      return;
    }

    // Clear existing buttons
    container.innerHTML = '';

    paypal.Buttons({
      style: {
        layout: 'horizontal',
        color: 'gold',
        shape: 'rect',
        label: 'paypal',
        height: 40,
      },

      // Create order on PayPal
      createOrder: async () => {
        if (!authService.isAuthenticated()) {
          throw new Error('Please log in to continue');
        }

        const accessToken = authService.getAccessToken();
        const response = await fetch(`${SUPABASE_URL}/functions/v1/create-paypal-order`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ productKey }),
        });

        if (!response.ok) {
          const errorBody = await response.json();
          if (errorBody.useStripe) {
            // Signal caller to fall back to Stripe instead of throwing
            if (options.onUseStripe) {
              options.onUseStripe();
            }
            // Throw to abort PayPal flow (PayPal SDK requires this)
            throw new Error('PAYPAL_USE_STRIPE');
          }
          throw new Error(errorBody.error || 'Failed to create order');
        }

        const data = await response.json();
        return data.orderId;
      },

      // Capture the order after approval
      onApprove: async (data) => {
        try {
          if (options.onProcessing) {
            options.onProcessing();
          }

          const accessToken = authService.getAccessToken();
          const response = await fetch(`${SUPABASE_URL}/functions/v1/capture-paypal-order`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderId: data.orderID }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to capture payment');
          }

          const result = await response.json();

          // Refresh entitlements
          entitlementService.clearCache();
          await entitlementService.getEntitlements(true);

          if (options.onSuccess) {
            options.onSuccess(result);
          }

        } catch (error) {
          console.error('[PayPalService] Capture error:', error);
          if (options.onError) {
            options.onError(error);
          }
        }
      },

      onCancel: () => {
        console.log('[PayPalService] Payment cancelled');
        if (options.onCancel) {
          options.onCancel();
        }
      },

      onError: (error) => {
        // Ignore sentinel error from useStripe redirect (already handled via onUseStripe)
        if (error?.message === 'PAYPAL_USE_STRIPE') return;
        console.error('[PayPalService] Error:', error);
        if (options.onError) {
          options.onError(error);
        }
      },
    }).render(`#${containerId}`);
  }
}

// Export singleton instance
export const paypalService = new PayPalService();
